import i18next from "i18next";

export interface IHttpResponse<T> extends Response {
  data?: T;
}

/**
 * Raised when fetch() itself fails — i.e. no HTTP response ever came back:
 * the server is offline/unreachable, DNS failed, or the browser blocked the
 * request at the CORS layer (including a failed preflight). Callers can branch
 * on `isNetworkError` to show a "service unavailable, try again" message
 * instead of the raw browser text ("Failed to fetch" / "Load failed"). HTTP
 * 4xx/5xx are NOT network errors — those resolve with a body and reject with it.
 */
export interface INetworkError extends Error {
  isNetworkError: true;
  originalError: unknown;
}

// i18n key (the app uses full English sentences as keys — keySeparator/
// nsSeparator are off). Localized in es/fr; other locales fall back to English.
// The `.message` is resolved at failure time, so it follows the active language.
export const NETWORK_ERROR_MESSAGE_KEY =
  "We couldn't reach the server. Please check your connection and try again in a moment.";

export const isNetworkError = (err: any): err is INetworkError =>
  !!err && err.isNetworkError === true;

const buildNetworkError = (originalError: unknown): INetworkError => {
  const localizedMessage = String(i18next.t(NETWORK_ERROR_MESSAGE_KEY));
  const baseError = originalError instanceof Error ? originalError : new Error(localizedMessage);
  const error = baseError as INetworkError;
  error.name = "NetworkError";
  error.message = localizedMessage;
  error.isNetworkError = true;
  error.originalError = originalError;
  return error;
};

export const http = <T>(request: RequestInfo): Promise<IHttpResponse<T>> => {
  return new Promise((resolve, reject) => {
    let response: IHttpResponse<T>;
    fetch(request)
      .then(res => {
        response = res;
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          return {} as IHttpResponse<T>;
        }
        return res.json();
      })
      .then(body => {
        if (response.ok) {
          response.data = body;
          resolve(response);
        } else {
          reject(body);
        }
      })
      .catch(err => {
        console.error(err);
        // Per the Fetch spec a connectivity failure rejects with a TypeError
        // and nothing else does: an HTTP error status resolves (handled above),
        // and a malformed-JSON body rejects as a SyntaxError. So a TypeError
        // here reliably means "no response reached us" — offline server, DNS
        // failure, or a CORS/preflight block, which are indistinguishable from
        // JS by design. Surface those as a typed, friendly network error.
        reject(err instanceof TypeError ? buildNetworkError(err) : err);
      });
  });
};

export const get = async <T>(
  path: string,
  args: RequestInit = {
    method: 'GET', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    credentials: 'include',
    headers: {
      CWB: '1'
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
  }
): Promise<IHttpResponse<T>> => {
  return await http<T>(new Request(path, args));
};

export const post = async <T>(
  path: string,
  body: any,
  args: RequestInit = {
    method: "post",
    mode: "cors",
    credentials: "include",
    // cache: "no-cache",
    headers: {
      "Content-Type": "application/json",
      CWB: "1"
    },
    body: JSON.stringify(body)
  }
): Promise<IHttpResponse<T>> => {
  return await http<T>(new Request(path, args));
};

export const patch = async <T>(
  path: string,
  body: any,
  args: RequestInit = {
    method: "PATCH",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      CWB: "1"
    },
    body: JSON.stringify(body)
  }
): Promise<IHttpResponse<T>> => {
  return await http<T>(new Request(path, args));
};

export const put = async <T>(
  path: string,
  body: any,
  args: RequestInit = { method: "put", body: JSON.stringify(body) }
): Promise<IHttpResponse<T>> => {
  return await http<T>(new Request(path, args));
};
